import { Component, OnInit, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import * as moment from 'moment-timezone';

@Component({
    selector: 'app-time-zone',
    templateUrl: './time-zone.component.html',
    styleUrls: ['./time-zone.component.scss']
})
export class TimeZoneComponent implements OnInit {
    @Output() childTimeZoneChanged = new EventEmitter<any>();
    @Output() getAbsoluteOffset = new EventEmitter<any>();
    @Output() getTimeZoneMixed = new EventEmitter<{ tz: string, offset: number, gmt: string }>();

    @Input() currentTimezone: string;
    @Input() currentOffset: number;
    options: Array<{ value: string, display: string }>;
    filteredOptions: Observable<{ value: string, display: string }[]>;
    myControl: UntypedFormControl;
    @ViewChild('autoCompleteInput', { read: MatAutocompleteTrigger, static: true })
    autoComplete: MatAutocompleteTrigger;

    constructor(private translate: TranslateService) { }

    ngOnInit() {
        this.initAllTZ();
        this.myControl = (this.currentTimezone && this.timezoneListContainValue(this.currentTimezone))
            ? new UntypedFormControl(this.tzFactory(this.currentTimezone)) :
            new UntypedFormControl(this.tzFactory(moment.tz.guess()));
        this.filteredOptions = this.myControl.valueChanges
            .pipe(
                startWith<string | { value: string, display: string, offstet: number }>(),
                map(value => typeof value === 'string' ? value : value.value),
                map(value => value ? this.filter(value) : this.options.slice())
            );
        this.childTimeZoneChanged.emit(this.myControl.value.value);
        this.getAbsoluteOffset.emit(this.myControl.value.offset);
        this.getTimeZoneMixed.emit(
            {
                tz: this.myControl.value.value,
                offset: this.myControl.value.offset,
                gmt: 'GMT: ' + this.myControl.value.offset
            });
    }
    /**
     * @description Cronstruct the array from moment-timezone lib
     * @returns {{value:string, display:string}[]}
     */
    initAllTZ() {
        let customTimezone = moment.tz.names();
        let customOptions: Array<{ value: string, display: string }> = new Array();
        customTimezone.forEach(element => {
            customOptions.push(this.tzFactory(element));
        });
        this.options = customOptions;
    }
    /**
     * @description Determine if the input value is correct (strictly), if not we set the default value
     * @return {boolean}
     */
    isValid() {
        if (!this.timezoneListContainValue((this.myControl.value))) {
            this.myControl.setValue((this.currentTimezone) ?
                (this.tzFactory(this.currentTimezone)) : (this.tzFactory(moment.tz.guess())));
        }
    }
    /**
     * @description return an object used to clarify timezone for the user
     * @param timezone like 'Europe/Paris'
     * @returns {{value:string, display:string}}
     */
    tzFactory(timezone: string): { value: string, display: string, offset: number } {
        return {
            display: '(GMT' + moment.tz(timezone).format('Z') + ') ' + timezone.replace(/_/g, ' '),
            value: timezone,
            offset: moment.tz(timezone).utcOffset()
        };
    }
    /**
     * @description check if the value of the timezone input is strictly equal to a value of the original timezone value array
     * @param tzDatas input value (that user typed inside or corresponding value of click event)
     * @returns {boolean}
     */
    timezoneListContainValue(tzDatas: string): boolean {
        return (this.options.filter(function (item) { return item.value === tzDatas; })).length === 1;
    }
    /**
     * @description Clear the timezone field if necessary and send a correct value to his parent
     * @param tzDatas Object send to the list
     */
    changeTimezone(tzDatas: { value: string, display: string, offset: number }) {
        if (!tzDatas || !this.timezoneListContainValue(tzDatas.value)) {
            this.myControl.setValue(this.tzFactory(this.currentTimezone));
        } else {
            this.currentTimezone = tzDatas.value;
            this.childTimeZoneChanged.emit(tzDatas.value);
            this.getAbsoluteOffset.emit(tzDatas.offset);
            this.getTimeZoneMixed.emit({ tz: tzDatas.value, offset: tzDatas.offset, gmt: 'GMT: ' + tzDatas.offset });

        }
    }
    /**
     * @description search correpsonding values in all the timezones availables
     * @param name literal sequence typed by user
     * @returns an array of object
     */
    filter(name: string): { value: string, display: string }[] {
        let filtered = this.options.filter(option =>
            option.value.toLowerCase().replace(/_/g, ' ').indexOf(name.toLowerCase()) !== -1);
        if (filtered.length === 0) {
            filtered = [{ value: 'empty', display: this.translate.instant('TIMEZONE.EMPTY') }];
        }
        return filtered;
    }
    displayFn(tz?: { value: string, display: string, offset: number }): string | undefined {
        return tz ? tz.display : undefined;
    }
    /**
     * @description Clear the timezone field
     */
    clearTemporarily() {
        this.myControl.setValue('');
    }
    /**
     * @description Detect user's click outside of this component, timezone field is filtered if
     * its value isn't correct
     */
    onClose() {
        if ((!this.timezoneListContainValue(this.myControl.value))) {
            this.myControl.setValue((this.currentTimezone) ?
                (this.tzFactory(this.currentTimezone)) : (this.tzFactory(moment.tz.guess())));
        }
    }
}
